import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import MostLikelyMorphologicCodesModal from '@/modules/configurationModule/detail/samples/mostLikelyDiagnoses/MostLikelyMorphologicCodesModal.vue'
import CodificationSearcherModal from '@/modules/configurationModule/detail/samples/CodificationSearcherModal.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import { IContentMostLikelyMorphologicCodesGrid, initialMostLikelyMorphologicCodesGridState } from '@/modules/configurationModule/detail/samples/mostLikelyDiagnoses/MostLikelyMorphologicCodes.models'
import { mostLikelyMorphologicCodesConfigurationServices } from '@/api/resources/configuration/samples/mostLikelyMorphologicCodes/MostLikelyMorphologicCodesServices'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        MostLikelyMorphologicCodesModal,
        CodificationSearcherModal,
        GridDisplayOptions
    }
})
export default class MostLikelyMorphologicCodesGridComponent extends BaseCrudServerSideComponent<IContentMostLikelyMorphologicCodesGrid> implements BaseCrud {
    @Ref() readonly mostLikelyMorphologicCodesModalWrapper!: any
    @Ref() readonly codificationSearcherModalWrapper!: any

    constructor() {
        super()
        this.state = initialMostLikelyMorphologicCodesGridState()
    }

    showCodificationSearcherModal() {
        this.codificationSearcherModalWrapper.show()
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:StrictNullable<RowNode> = params?.node
                    const selectedRow:IContentMostLikelyMorphologicCodesGrid = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update').toString(), selectedRow, this.mostLikelyMorphologicCodesModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data?.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }

    async createOperation(data: IContentMostLikelyMorphologicCodesGrid) {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(mostLikelyMorphologicCodesConfigurationServices.createMostLikelyMorphologicCodes(data))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.mostLikelyMorphologicCodesModalWrapper.hide()
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.reloadData()
    }

    async patchOperation(data: IContentMostLikelyMorphologicCodesGrid[]) {
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(mostLikelyMorphologicCodesConfigurationServices.patchMostLikelyMorphologicCodesBatch(data))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.mostLikelyMorphologicCodesModalWrapper.hide()
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.reloadData()
    }

    async activateDeactivateSelection(option: boolean, selectionBatch: IContentMostLikelyMorphologicCodesGrid[]) {
        const mostLikelyMorphologicCodesToPatch = selectionBatch?.filter((mostLikelyMorphologicCode: IContentMostLikelyMorphologicCodesGrid) => mostLikelyMorphologicCode.active !== option)?.map((mostLikelyMorphologicCode:IContentMostLikelyMorphologicCodesGrid) => {
            return {
                ...mostLikelyMorphologicCode,
                active: option
            }
        })
        if (mostLikelyMorphologicCodesToPatch.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(mostLikelyMorphologicCodesConfigurationServices.patchMostLikelyMorphologicCodesBatch(mostLikelyMorphologicCodesToPatch))
            this.$_updateLoading(false)
            if (err) {
                this.$_showErrorModalFromErrorResponse(err)
                return
            }
            if (selectionBatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: selectionBatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }

    async deleteBatchOperation(mostLikelyMorphologicCodes: IContentMostLikelyMorphologicCodesGrid[]) {
        const mostLikelyMorphologicCodesToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: mostLikelyMorphologicCodes.map((mostLikelyMorphologicCode: IContentMostLikelyMorphologicCodesGrid) => Number(mostLikelyMorphologicCode?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(mostLikelyMorphologicCodesConfigurationServices.deleteMostLikelyMorphologicCodesBatch(mostLikelyMorphologicCodesToDelete))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (mostLikelyMorphologicCodes.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: mostLikelyMorphologicCodes?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }

    async deleteOperation(mostLikelyMorphologicCodes: IContentMostLikelyMorphologicCodesGrid[]) {
        if (mostLikelyMorphologicCodes.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const promise = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (promise) {
                this.deleteBatchOperation(mostLikelyMorphologicCodes)
            }
        }
    }
}
